import type { RoomStateObject } from '@/hooks/types';
import type { ViewerVenue } from './replyReachability';

export interface TagRefusal {
  reachable: boolean;
  /** The bold refusal sentence (demo Screen 3's `.refusal strong`). */
  reason?: string;
  /** The quieter follow-on line naming where to go instead (`.refusal .fix`). */
  hint?: string;
}

const REACHABLE: TagRefusal = { reachable: true };

/**
 * Mirrors the server's own copy verbatim
 * (`world.scenes.interaction_services._TARGET_UNREACHABLE_HINT` /
 * `_describe_unreachable_targets`), rather than inventing client-side wording,
 * exactly as `replyReachability` already does for the reply case.
 */
const TARGET_UNREACHABLE_HINT =
  'Address the room to reach them, or send a whisper. Your draft is kept.';
// The threshold (#3867): mirrors the server's `_THRESHOLD_HINT` verbatim.
const THRESHOLD_HINT =
  'They can be addressed after their first pose, or reached by a whisper. Your draft is kept.';

function describeThresholdTargets(names: string[]): string {
  if (names.length === 1) {
    return `${names[0]} has not joined the scene yet.`;
  }
  const joined = names.slice(0, -1).join(', ') + ` and ${names[names.length - 1]}`;
  return `${joined} have not joined the scene yet.`;
}
function describeUnreachableTargets(names: string[]): string {
  if (names.length === 1) {
    return `${names[0]} is across the room and will not see table talk.`;
  }
  const joined = names.slice(0, -1).join(', ') + ` and ${names[names.length - 1]}`;
  return `${joined} are across the room and will not see table talk.`;
}

/**
 * Client-side mirror of `persona_can_receive`'s reachability rules, checked
 * against `composerMode.targets` (#3810) rather than the composer's free-text
 * prose (which is never sent to the server as a target at all). `mode` is
 * `composerMode.command`: whisper is always reachable for its own named
 * targets (receiver-based, never location-based, mirroring
 * `persona_can_receive`'s whisper branch), and so is a page (#4129), which is
 * OOC and reaches its correspondent wherever they stand; every other mode
 * checks physical presence only (room-heard), except `tt` (Tabletalk), which
 * is Place-scoped and additionally requires the target share the actor's own
 * current Place.
 */
export function tagReachability(
  targetNames: string[],
  roomCharacters: RoomStateObject[],
  mode: string,
  venue: ViewerVenue
): TagRefusal {
  if (targetNames.length === 0 || mode === 'whisper' || mode === 'page') {
    return REACHABLE;
  }

  const byLowerName = new Map(
    roomCharacters.map((character) => [character.name.toLowerCase(), character])
  );

  // Present in the room but not yet in the scene (#3867): a room-heard address
  // waits for their first pose. Checked before the place rule below because a
  // threshold character has no seat in the scene either way.
  const threshold = targetNames.filter(
    (name) => byLowerName.get(name.toLowerCase())?.in_scene === false
  );
  if (threshold.length > 0) {
    return {
      reachable: false,
      reason: describeThresholdTargets(threshold),
      hint: THRESHOLD_HINT,
    };
  }

  const unreachable = targetNames.filter((name) => {
    const character = byLowerName.get(name.toLowerCase());
    if (!character) {
      // Not physically in the room at all: unreachable regardless of mode.
      return true;
    }
    if (mode === 'tt' && venue.isAtPlace) {
      return character.place_id !== venue.currentPlaceId;
    }
    // Room-heard (pose, say, emit, ...): being found in roomCharacters already
    // satisfies the physical-presence requirement, regardless of the actor's
    // own seating.
    return false;
  });

  if (unreachable.length === 0) {
    return REACHABLE;
  }

  return {
    reachable: false,
    reason: describeUnreachableTargets(unreachable),
    hint: TARGET_UNREACHABLE_HINT,
  };
}
